import {
  downloadDocument,
  downloadFolder,
  downloadUserDocument,
} from '@/_api/client-documents';
import { downloadUpload } from '@/_api/drop-link';
import { downloadDropLinkDocument } from '@/_api/firm';
import { DropLinksContext } from '@/_contexts/DropLinksProvider';
import { saveAs } from '@/_utils/files';
import type { Notification } from '@/_types/models/notifications';
import { useContext } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { NotificationsContext } from '@/_contexts/admin/NotificationsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  NOTIFICATIONS_DATA,
  BROAD_NOTIFICATION_TYPES,
  BROAD_NOTIFICATION_TYPES_SET,
  CATEGORY_ORDER,
} from '@/_constants/notifications';
import { NotificationTypesEnum } from '@/_types/notifications';

export const isDownloadLink = (link: string): boolean => {
  const isConversationLink = link.includes('!conversation/');
  const isFolderDownloadLink = link.includes('!download_folder/');
  const isDropLink = link.includes('!download_drop_link_upload/');
  const isOldDropLink = link.includes('!download_drop_link_document/');
  const isSingleFileDownloadLink = link.includes('!download/');

  return (
    isConversationLink ||
    isFolderDownloadLink ||
    isDropLink ||
    isOldDropLink ||
    isSingleFileDownloadLink
  );
};

export const useDownloadNotification = () => {
  const { token } = useContext(UserContext);
  const { dismissNotificationItem } = useContext(NotificationsContext);
  const { addToast } = useContext(ToastContext);
  const { loadDropLinks } = useContext(DropLinksContext);
  const downloadNotification = async (
    notification: Notification,
  ): Promise<boolean> => {
    if (!notification?.link) return false;
    if (!token) {
      addToast('error', 'Please refresh the page');
      return false;
    }

    const link = notification.link;
    const isFolderDownload = link.includes('!download_folder/');
    const isDropLink = link.includes('!download_drop_link_upload/');
    const isOldDropLink = link.includes('!download_drop_link_document/');
    const isDownloadLink = link.includes('!download/');

    // case: file
    if (isDownloadLink) {
      let response;

      const params: any = {
        token,
        link: link
          .replace('!download//Soraban', '')
          .replace('!download//soraban', ''),
      };

      if (notification.company_id) {
        params.companyId = notification.company_id;
        response = await downloadDocument(params);
      } else {
        response = await downloadUserDocument(params);
      }

      if (response?.error) {
        throw new Error(response.error);
      }

      const document = response?.document;

      if (document?.data?.link) {
        await saveAs(document.data.link);
        dismissNotificationItem(notification.id);
        return true;
      }

      throw new Error('No download link found in response');
    }

    // case: folder
    if (isFolderDownload) {
      try {
        const cleanLink = link
          .replace('!download_folder//Soraban', '')
          .replace('!download_folder//soraban', '');

        await downloadFolder({
          companyId: notification.company_id,
          link: cleanLink,
          name: cleanLink.split('/')[cleanLink.split('/').length - 2],
          token,
        });
        dismissNotificationItem(notification.id);
        return true;
      } catch (error: any) {
        // Note from @jordan - A 503 means that the file size is too big.
        // I'm not sure if that's from AWS or Heroku or what. But that is
        // confirmed here: https://linear.app/soraban/issue/SOR-6869
        if (error?.response?.status === 503) {
          throw new Error(
            "Unfortunately, that folder/file is too large to download. Can you split it up into smaller batches? Generally, the total size should be less than 150MB. And if you're converting images to PDF, it should be less than 15MB.",
          );
        }
        throw error;
      }
    }

    // case: drop link
    if (isOldDropLink) {
      // deprecated version of drop link
      const cleanLink = link.replace('!download_drop_link_document/', '');
      const folderName = cleanLink.split('/')[cleanLink.split('/').length - 1];
      await downloadDropLinkDocument({
        folderName,
        token,
      });
      dismissNotificationItem(notification.id);
      return true;
    }

    if (isDropLink) {
      try {
        // link looks like !download_drop_link_upload/drop-link-id=123&upload-id=456
        const cleanLink = link.replace('!download_drop_link_upload/', '');
        const dropLinkId = parseInt(cleanLink.split('=')[1]);
        const uploadId = parseInt(cleanLink.split('=')[2]);
        await downloadUpload({
          dropLinkId,
          uploadId,
          token,
        });
        dismissNotificationItem(notification.id);
        loadDropLinks();
        return true;
      } catch (e) {
        addToast('error', 'Failed to download upload');
        return false;
      }
    }

    return false;
  };

  return downloadNotification;
};

/**
 * Gets notifications in the correct order based on whether they should be
 * broad or granular, filtering out any that shouldn't be shown.
 */
export const getOrderedNotifications = (options: {
  isGranular: boolean;
  excludeConversation?: boolean;
  questionnaireDocumentEnabled?: boolean;
}): Array<{
  id: number;
  data: (typeof NOTIFICATIONS_DATA)[NotificationTypesEnum];
}> => {
  const { isGranular, excludeConversation, questionnaireDocumentEnabled } =
    options;

  if (!isGranular) {
    return BROAD_NOTIFICATION_TYPES.filter(
      (id) =>
        !excludeConversation ||
        id !== NotificationTypesEnum.conversation_related,
    ).map((id) => ({
      id,
      data: NOTIFICATIONS_DATA[id],
    }));
  }

  return Object.entries(NOTIFICATIONS_DATA)
    .filter(([key]) => {
      const id = parseInt(key, 10);

      if (BROAD_NOTIFICATION_TYPES_SET.has(id)) return false;

      if (
        excludeConversation &&
        id === NotificationTypesEnum.conversation_related
      ) {
        return false;
      }

      if (id === NotificationTypesEnum.drop_link_related) {
        return false;
      }

      if (
        id === NotificationTypesEnum.questionnaire_document_related &&
        !questionnaireDocumentEnabled
      ) {
        return false;
      }

      return true;
    })
    .map(([key, data]) => ({
      id: parseInt(key, 10),
      data,
    }))
    .sort((a, b) => {
      const categoryA = CATEGORY_ORDER.indexOf(a.data.category);
      const categoryB = CATEGORY_ORDER.indexOf(b.data.category);

      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }

      return a.data.name.localeCompare(b.data.name);
    });
};
